import { useDeferredValue, useMemo, useState } from "react";
import { Alert, Stack, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";

import { routePaths } from "../../../../routes/navigation";
import type { OrderRow } from "../../../../types";
import OrdersMetrics from "../../../orders/components/OrdersMetrics";
import OrdersWorkspace from "../../../orders/components/OrdersWorkspace";
import { orderChannelFilters } from "../../../orders/lib/helpers";
import { filterOrdersTableRows } from "../../../orders/lib/ordersPageFilters";

type Props = {
    orders: OrderRow[];
};

export default function Client360OrdersTab({ orders }: Props) {
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down("md"));
    const navigate = useNavigate();
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("Всички");
    const [channelFilter, setChannelFilter] = useState<(typeof orderChannelFilters)[number]>("Всички");
    const [selectedOrderNumber, setSelectedOrderNumber] = useState("");
    const [ordersPage, setOrdersPage] = useState(0);
    const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(25);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const normalizedSearch = useDeferredValue(searchTerm).trim().toLowerCase();

    const orderStatuses = useMemo(
        () => Array.from(new Set(orders.map((order) => order.status))).filter(Boolean),
        [orders],
    );
    const filteredOrders = useMemo(
        () => filterOrdersTableRows(orders, {
            channelFilter,
            contextualCustomerName: "",
            normalizedSearch,
            statusFilter,
            dateFrom,
            dateTo,
            traderFilter: "Всички",
            regionFilter: "Всички",
        }),
        [channelFilter, dateFrom, dateTo, normalizedSearch, orders, statusFilter],
    );
    const pagedOrders = filteredOrders.slice(
        ordersPage * ordersRowsPerPage,
        ordersPage * ordersRowsPerPage + ordersRowsPerPage,
    );
    const selectedOrder = filteredOrders.find((row) => row.orderNumber === selectedOrderNumber)
        ?? filteredOrders[0];
    const totalValue = filteredOrders.reduce((sum, row) => sum + row.value, 0);
    const attributedOrders = filteredOrders.filter((row) => row.campaign !== "Без кампания").length;
    const attributedShare = filteredOrders.length > 0
        ? `${Math.round((attributedOrders / filteredOrders.length) * 100)}%`
        : "0%";
    const readyForWarehouse = filteredOrders.filter((row) => row.status === "Подготвя се").length;
    const waitingForExpo = filteredOrders.filter((row) => row.sync === "Чака поток на едро").length;

    function openOrderItemsWindow(order: OrderRow) {
        const currentPath = `${location.pathname}${location.search}`;
        const params = new URLSearchParams({ order: order.orderNumber, returnTo: currentPath });
        navigate({ pathname: routePaths.orderItems, search: `?${params.toString()}` });
    }

    if (orders.length === 0) {
        return <Alert severity="info">Няма намерени поръчки за този клиент.</Alert>;
    }

    return (
        <Stack spacing={2}>
            <OrdersMetrics
                attributedShare={attributedShare}
                filteredCount={filteredOrders.length}
                readyForWarehouse={readyForWarehouse}
                totalValue={totalValue}
            />
            <OrdersWorkspace
                channelFilter={channelFilter}
                dateFrom={dateFrom}
                dateTo={dateTo}
                traderFilter="Всички"
                regionFilter="Всички"
                traderOptions={[]}
                regionOptions={[]}
                showTraderFilter={false}
                onDateFromChange={(value) => { setDateFrom(value); setOrdersPage(0); }}
                onDateToChange={(value) => { setDateTo(value); setOrdersPage(0); }}
                onTraderChange={() => undefined}
                onRegionChange={() => undefined}
                isCompact={isCompact}
                lastUpdatedAt=""
                onChannelChange={(value) => { setChannelFilter(value); setOrdersPage(0); }}
                onOpenOrderItems={openOrderItemsWindow}
                onPageChange={setOrdersPage}
                onRowsPerPageChange={(rowsPerPage) => { setOrdersRowsPerPage(rowsPerPage); setOrdersPage(0); }}
                onSearchChange={(value) => { setSearchTerm(value); setOrdersPage(0); }}
                onSelectOrder={setSelectedOrderNumber}
                onStatusChange={(value) => { setStatusFilter(value); setOrdersPage(0); }}
                orderStatuses={orderStatuses}
                orders={pagedOrders}
                pagination={{ page: ordersPage, rowsPerPage: ordersRowsPerPage, total: filteredOrders.length }}
                searchTerm={searchTerm}
                selectedOrderNumber={selectedOrder?.orderNumber}
                statusFilter={statusFilter}
                unattributedCount={filteredOrders.length - attributedOrders}
                waitingForExpo={waitingForExpo}
            />
        </Stack>
    );
}
